import type { Module } from '@pc/core';
import { appBus } from '@pc/core';
import { getClientSummary, probeSchemaIsExposed } from '@pc/db';
import { lazyWithReload } from '@pc/ui';
import './events';

// Use `lazyWithReload` so a stale-shell operator navigating into the
// Clients module after a deploy auto-recovers via a debounced reload
// instead of crashing on the 404'd chunk. See packages/ui/src/lazyWithReload.ts.
const ClientsListPage = lazyWithReload(() => import('./pages/ClientsListPage'));
const ClientDetailPage = lazyWithReload(() => import('./pages/ClientDetailPage'));
const ClientNewPage = lazyWithReload(() => import('./pages/ClientNewPage'));
const ClientInboxListPage = lazyWithReload(
  () => import('./pages/ClientInboxListPage'),
);
const ClientInboxThreadPage = lazyWithReload(
  () => import('./pages/ClientInboxThreadPage'),
);
const ClientsDashboardWidget = lazyWithReload(
  () => import('./widgets/ClientsDashboardWidget'),
);

export const clientsModule: Module = {
  id: 'clients',
  name: 'Clients',
  description: 'People you do business with. The source of truth for everything downstream.',
  basePath: '/clients',
  // Inbox routes precede the `/:id` parameterized route so /inbox doesn't
  // get matched as a UUID. React Router v6 matches statics-first, so the
  // ordering here is belt-and-suspenders documentation of intent.
  routes: [
    { path: '/', element: <ClientsListPage /> },
    { path: '/inbox', element: <ClientInboxListPage /> },
    { path: '/inbox/:threadId', element: <ClientInboxThreadPage /> },
    { path: '/new', element: <ClientNewPage /> },
    { path: '/:id', element: <ClientDetailPage /> },
  ],
  navItems: [
    { label: 'Clients', to: '/clients', order: 10 },
    // Inbox sits directly under Clients in the sidebar so operators
    // discover it through the same surface where they manage the
    // underlying records. The label keeps the order column ahead of
    // 'Wholesalers' (the next entry) by reusing 11.
    { label: 'Inbox', to: '/clients/inbox', order: 11 },
  ],
  dashboardWidgets: [
    {
      id: 'recent-clients',
      span: 2,
      order: 10,
      render: ClientsDashboardWidget,
    },
  ],
  events: {
    publishes: ['client.created', 'client.updated', 'client.archived'],
    subscribesTo: {
      // Example: log every emitted event in dev. Real subscribers (e.g., the
      // future Orders module) would react to 'client.archived' to flag open
      // orders. The handler runs in-app; it does not bypass RLS.
      'client.created': (payload) => {
        appBus().emit('app.toast', {
          text: `New client: ${payload.displayName}`,
          tone: 'success',
        });
      },
    },
  },
  // The single sanctioned cross-module surface.
  publicApi: {
    /** Look up a client through the stable v_client_summary view. */
    getClientSummary: getClientSummary as unknown as (...args: never[]) => unknown,
  },
  healthProbe: () => probeSchemaIsExposed('clients', 'v_client_summary'),
};
